import { useNavigate } from 'react-router-dom';
import type { BiomarkerResult } from '@/api/generated-catch-backend/model';
import { BiomarkerClassification } from '@/api/generated-catch-backend/model';
import { PortableText } from '@/app/components/questionnaireV2/components/portable-text/portable-text';
import { show as IntercomShow } from '@intercom/messenger-js-sdk';
import { BiomarkerBadge } from './biomarker-badge';
import { formatBiomarkerNotes } from '../../utils/format-biomarker-notes';

interface BiomarkerItemCardProps {
  biomarker: BiomarkerResult;
}

const classificationConfig = {
  [BiomarkerClassification.optimal]: {
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
  },
  [BiomarkerClassification.normal]: {
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
  },
  [BiomarkerClassification.out_of_range]: {
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
  },
  [BiomarkerClassification.critical]: {
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
  },
  [BiomarkerClassification.no_results]: {
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
  },
};

export function BiomarkerItemCard({ biomarker }: BiomarkerItemCardProps) {
  const navigate = useNavigate();
  const hasResult = biomarker.resultValue !== null && biomarker.resultValue !== undefined;
  const isPending = biomarker.classification === BiomarkerClassification.pending;
  const hasNoResults = biomarker.classification === BiomarkerClassification.no_results;

  const handleClick = () => {
    navigate(`/labs/biomarkers/${biomarker.metadata.slug}`);
  };

  // Function to split result and unit for proper styling
  const formatResultWithUnit = () => {
    if (!biomarker.resultValue) return null;

    const result = biomarker.resultValue.result;
    const unit = biomarker.resultValue.unit;
    const resultType = biomarker.resultValue.resultType;

    // For comment types, show value from notes field (stripped of leading/trailing whitespace/periods/linebreaks)
    if (resultType === 'comment') {
      const displayValue = formatBiomarkerNotes(biomarker.resultValue.notes);
      // Display nothing if notes is empty after formatting
      return displayValue ? <span className="font-bold">{displayValue}</span> : null;
    }

    // Special case: if unit is %, show descriptive text with bold value
    if (unit === '%') {
      const biomarkerName = biomarker.metadata.displayName.toLowerCase();
      return (
        <>
          <span className="font-normal">Your {biomarkerName} percentage is </span>
          <span className="font-bold">{result}%</span>
        </>
      );
    }

    // Split the result to separate numeric part from descriptive text (like "thousand", "million")
    // Only the numeric value (with optional < or > prefix) should be bold, everything else should be normal weight
    const match = result.match(/^([<>]?\d+(?:\.\d+)?)\s*(.*)$/);

    if (match) {
      const numericPart = match[1]; // e.g., "89", "<30"
      const descriptivePart = match[2]; // e.g., "thousand"

      return (
        <>
          <span className="font-bold">{numericPart}</span>
          <span className="font-normal">
            {descriptivePart && ` ${descriptivePart}`}
            {unit && ` ${unit}`}
          </span>
        </>
      );
    }

    // Fallback if no numeric match
    return (
      <>
        <span className="font-bold">{result}</span>
        {unit && <span className="font-normal"> {unit}</span>}
      </>
    );
  };

  return (
    <div className="w-full rounded-lg bg-white px-5 py-3 md:p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {/* Header with name and badge */}
          <div className="mb-2 flex flex-col items-start gap-2 md:flex-row md:flex-wrap md:items-center">
            <h4 className="text-base font-semibold leading-[130%] text-catch-dark-blue-90">{biomarker.metadata.displayName}</h4>
            <BiomarkerBadge classification={biomarker.classification} />
          </div>

          {/* Result value */}
          {hasResult && biomarker.resultValue && (
            <div className="mb-2 text-sm leading-[140%] text-catch-dark-blue-90">{formatResultWithUnit()}</div>
          )}

          {/* Contact Support for missing results (only when results are ready) or Summary content */}
          {biomarker.classification === BiomarkerClassification.no_results ? (
            <span
              onClick={(e) => {
                e.stopPropagation();
                IntercomShow();
              }}
              className="cursor-pointer text-sm font-bold leading-[140%] text-catch-dark-blue-90 underline"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  IntercomShow();
                }
              }}
            >
              Contact Customer Support
            </span>
          ) : (
            biomarker.summaryContent &&
            biomarker.summaryContent.length > 0 && (
              <div className="text-sm text-gray-600 md:block">
                <PortableText content={biomarker.summaryContent} inline />
              </div>
            )
          )}
        </div>

        {/* Arrow icon - only shown when not pending and has results */}
        {!isPending && !hasNoResults && (
          <button
            onClick={handleClick}
            className="flex h-[39px] w-[39px] shrink-0 items-center justify-center rounded-full bg-[#F0F5F6] transition-opacity hover:opacity-70"
            type="button"
            aria-label={`View details for ${biomarker.metadata.displayName}`}
          >
            <img src="/images/icons/arrow right.svg" alt="" className="h-[9px] w-[15px]" />
          </button>
        )}
      </div>
    </div>
  );
}
